import { CloseRounded } from '@mui/icons-material'
import {
	Dialog,
	DialogActions,
	DialogContent,
	type DialogProps,
	DialogTitle,
	IconButton,
	Typography
} from '@mui/material'
import { refetch as refetchImports } from 'features/imports/utils'
import isEmpty from 'lodash/isEmpty'
import { type FC, type ReactElement, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from 'shared/ui'

import { contacts } from '../../../../../actions/contacts'
import {
	BadGatewayError,
	NotAuthorizedError,
	notifyError
} from '../../../../../shared/errors'
import { useAppSelector } from '../../../../../shared/hooks'
import { EXPORT_CONTACTS } from '../../constants.ts'
import type {
	TExportContactsOption,
	TExportContactsType,
	TExportContentProps,
	TExportedProfile,
	TExportEvent,
	TExportFinishedUpload,
	TExportFromUrlTypes,
	TExportState
} from '../../types.ts'
import { ExportComplete } from '../ExportComplete'
import { ExportFileUploaded } from '../ExportFileUploaded'
import { ExportFromCsv } from '../ExportFromCsv'
import { ExportFromProfileUrls } from '../ExportFromProfileUrls'
import ExportFromMyConnections from '../ExportFromMyConnections'
import { ExportFromUrl } from '../ExportFromUrl'
import { ExportPicker } from '../ExportPicker'
import { ExportProgress } from '../ExportProgress'
import { ExportStep } from '../ExportStep'
import styles from './ExportModal.styles'
import { createProxyUnavailableError } from '../../../../../shared/errors/ProxyUnavailableError'

const MAX_STEPS = 3
const EXPORT_CONTACTS_OPTIONS =
	EXPORT_CONTACTS.OPTIONS as TExportContactsOption[]

export type TExportModalProps = Omit<DialogProps, 'onClose'> & {
	onClose: () => void
}

const EXPORT_INNER_CONTENT_MAP: Record<
	TExportContactsType,
	FC<TExportContentProps>
> = {
	linkedinCustomizedProfileUrl: ExportFromCsv,
	linkedInEvent: ExportFromUrl,
	linkedInGroup: ExportFromUrl,
	linkedInMyConnections: ExportFromMyConnections,
	linkedinProfileUrl: ExportFromProfileUrls,
	linkedInSearch: ExportFromUrl,
	recruiterSearch: () => <div>Export from recruiter search</div>, // TODO: Implement this component
	salesNavigator: ExportFromUrl
}

const ExportModal: FC<TExportModalProps> = ({
	onClose,
	open,
	...rest
}: TExportModalProps): ReactElement => {
	const { appExportConnector } = useAppSelector(state => state.app)
	const profilesExport = appExportConnector?.profilesExport

	const [step, setStep] = useState(0)
	const [exportType, setExportType] = useState<null | TExportContactsType>(null)
	const [dataToExport, setDataToExport] = useState<
		null | string | TExportedProfile[]
	>(null)
	const [contactsCount, setContactsCount] = useState<null | number>(null)

	const navigate = useNavigate()
	const location = useLocation()

	const [startUploading, setStartUploading] = useState(false)
	const [uploadingProgress, setUploadingProgress] = useState(0)
	const [finishedUploading, setFinishedUploading] =
		useState<TExportFinishedUpload>({})
	const [errorUploadingData, setErrorUploadingData] = useState({})
	const [isFileUploaded, setIsFileUploaded] = useState(false)

	const onPrevStep = async () => {
		if (step === 2 && profilesExport) {
			await profilesExport.stop()
			return
		}

		setStep(prevStep => {
			return Math.max(prevStep - 1, 0)
		})
	}

	const onNextStep = () => {
		setStep(prevStep => {
			if (prevStep === 0) {
				setContactsCount(null)
				setDataToExport(null)
			}
			if (prevStep === 1) {
				setUploadingProgress(0)
				startUpload()
				setIsFileUploaded(false)
			}
			return Math.min(prevStep + 1, MAX_STEPS - 1)
		})
	}

	const isNextEnabled =
		(step === 0 && exportType) ||
		(step === 1 && contactsCount && contactsCount > 0)

	const pickedOption = exportType
		? EXPORT_CONTACTS_OPTIONS.find(option => option.value === exportType)
		: undefined

	const ExportInnerContent = pickedOption
		? EXPORT_INNER_CONTENT_MAP[pickedOption.value]
		: () => <div />

	const startUpload = () => {
		if (!profilesExport || !exportType || !dataToExport) {
			return
		}

		if (exportType === 'linkedInMyConnections') {
			profilesExport.linkedInMyConnections
				.start(pickedOption?.url || '')
				.catch(notifyError)
			return
		}

		if (
			[
				'linkedInEvent',
				'linkedInGroup',
				'linkedInSearch',
				'salesNavigator'
			].includes(exportType) &&
			typeof dataToExport === 'string'
		) {
			profilesExport[exportType as TExportFromUrlTypes]
				.start(dataToExport)
				.catch(notifyError)

			return
		}

		if (
			['linkedinCustomizedProfileUrl', 'linkedinProfileUrl'].includes(
				exportType
			) &&
			Array.isArray(dataToExport)
		) {
			profilesExport.startCSV(dataToExport).catch(notifyError)
			return
		}
	}

	const callback = async (event: TExportEvent) => {
		const exportState = (await profilesExport?.getState()) as
			| TExportState
			| null
			| undefined

		if (!exportState) {
			return
		}

		switch (event) {
			case 'ExportChangedHandler':
				setUploadingProgress(exportState.processed ?? 0)
				break
			case 'ExportCompletedHandler':
				setFinishedUploading(exportState)
				location.pathname.includes('/contacts') &&
					(await contacts.getRecentImports())
				refetchImports()
				break
			case 'ExportFailedHandler': {
				let exportError
				switch (exportState.exportError?.name) {
					case 'BadGatewayError':
						exportError = new BadGatewayError('Bad gateway')
						break

					case 'NotAuthorizedError':
						exportError = new NotAuthorizedError('Not authorized')
						break

					case 'ProxyUnavailableError':
						exportError = createProxyUnavailableError()
						break

					default:
						exportError = new Error('Export is failed')
				}

				notifyError(exportError)
				setErrorUploadingData(exportState)
				location.pathname.includes('/contacts') &&
					(await contacts.getRecentImports())
				refetchImports()
				break
			}
			case 'ExportStartedHandler':
				setStartUploading(true)
				break

			default:
				break
		}
	}

	useEffect(() => {
		profilesExport?.addExportStartingHandler(() =>
			callback('ExportStartingHandler')
		)
		profilesExport?.addExportStartedHandler(() => {
			callback('ExportStartedHandler')
		})
		profilesExport?.addExportChangedHandler(() => {
			callback('ExportChangedHandler')
		})
		profilesExport?.addExportFailedHandler(() =>
			callback('ExportFailedHandler')
		)
		profilesExport?.addExportCompletedHandler(() =>
			callback('ExportCompletedHandler')
		)

		return () => {
			profilesExport?.removedAllHandlers()
		}
	}, [profilesExport])

	const isExportCompleted =
		!isEmpty(finishedUploading) || !isEmpty(errorUploadingData)

	const isDialogTitleAndActionsVisible = !isExportCompleted && !isFileUploaded
	const isCloseIconVisible = !isExportCompleted && !startUploading

	const onShowResult = () => {
		if (isEmpty(finishedUploading)) {
			return
		}

		profilesExport?.clear()
		onClose()
		if (finishedUploading.navigationUrl) {
			navigate(
				`/contacts/import/${finishedUploading.navigationUrl.split('/').pop()}`
			)
		}
	}

	const onFileReset = () => {
		setDataToExport(null)
		setContactsCount(null)
		setIsFileUploaded(false)
	}

	return (
		<Dialog
			fullWidth={true}
			open={open}
			sx={styles.dialog(step, isExportCompleted)}
			{...rest}
		>
			{isCloseIconVisible && (
				<IconButton aria-label='close' onClick={onClose} sx={styles.close}>
					<CloseRounded />
				</IconButton>
			)}
			{isDialogTitleAndActionsVisible && (
				<DialogTitle sx={styles.header}>
					<Typography sx={styles.title}>Import Contacts</Typography>
					<Typography sx={styles.info}>
						Step {step + 1} / {MAX_STEPS}
					</Typography>
				</DialogTitle>
			)}

			<DialogContent sx={styles.content}>
				{isExportCompleted && (
					<ExportComplete
						onShowResult={onShowResult}
						processedCount={finishedUploading.processed}
						uploadedCount={finishedUploading.exported}
					/>
				)}
				{!isExportCompleted && (
					<>
						{step === 0 && (
							<ExportStep title='How do you want to import contacts?'>
								<ExportPicker
									exportType={exportType}
									setExportType={setExportType}
								/>
							</ExportStep>
						)}
						{step === 1 && pickedOption && (
							<>
								{isFileUploaded ? (
									<ExportFileUploaded
										contactsCount={contactsCount}
										onReset={onFileReset}
										onUpload={onNextStep}
									/>
								) : (
									<ExportInnerContent
										contactsCount={contactsCount}
										option={pickedOption}
										setContactsCount={setContactsCount}
										setDataToExport={setDataToExport}
										setIsFileUploaded={setIsFileUploaded}
									/>
								)}
							</>
						)}
						{step === 2 && (
							<ExportStep title='Processing'>
								{startUploading && (
									<ExportProgress
										uploadingProgress={uploadingProgress}
										uploadingTotal={contactsCount}
									/>
								)}
							</ExportStep>
						)}
					</>
				)}
			</DialogContent>
			{isDialogTitleAndActionsVisible && (
				<DialogActions disableSpacing sx={styles.actions}>
					{step > 0 && (
						<Button onClick={onPrevStep} variant='bordered-dark'>
							{step === 2 ? 'Stop' : 'Back'}
						</Button>
					)}

					{step < MAX_STEPS - 1 && (
						<Button
							disabled={!isNextEnabled}
							onClick={onNextStep}
							variant='primary'
						>
							Next
						</Button>
					)}
				</DialogActions>
			)}
		</Dialog>
	)
}

export default ExportModal
